/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { omit } from 'lodash-es';

import {
  selectArnmMaintenanceState,
  resetModalArnmMaintenanceChange,
  filtersModalConditionArnmMaintenanceChange,
  filtersModalRulesArnmMaintenanceChange,
} from '@store';
import { getCleanedUrlParams, showError } from '@utils';
import { BOARDS_ID } from '@constants';
import { useQuery } from 'react-query';
import { QUERY_KEYS } from 'constants/queryKeys';
import { boardsServices } from '@services';
import { useAssignees, useServicePoints } from 'hooks/common';
import {
  getArnmMaintenanceColumns,
  getArnmMaintenancePayload,
  handleArnmMaintenanceData,
  normalizeOrderBy,
} from './utils';

export const ARNM_MAINTENANCE_PAGE_SIZE = 500;
export const ARNM_MAINTENANCE_EXPORT_LIMIT = 100000;

export const useArnmMaintenanceOperations = () => {
  const dispatch = useDispatch();
  const { filters } = useSelector(selectArnmMaintenanceState);
  const router = useRouter();
  const {
    assignees,
    assigneesWithNone,
    isFetching: isAssigneesFetching,
    isFetched: isAssigneesFetched,
  } = useAssignees();
  const { servicePoints, isFetching: isServicePointsFetching } =
    useServicePoints();

  const page = router.query?.page || 1;
  const pageSize = router.query?.pageSize || ARNM_MAINTENANCE_PAGE_SIZE;
  const orderBy = router.query?.orderBy || 'createdAt';
  const order = router.query?.order || 'desc';
  const query = router.query?.query;

  const cleanedParams = getCleanedUrlParams({
    ...omit(router.query, ['view']),
    page,
    pageSize: parseInt(pageSize, 10) || ARNM_MAINTENANCE_PAGE_SIZE,
    orderBy: normalizeOrderBy(orderBy),
    order,
    query,
  });

  const maintenanceResponse = useQuery(
    [
      QUERY_KEYS.GET_ARNM_MAINTENANCE_TASKS_KEY,
      { cleanedParams, filters: filters.modalFiltersRules },
    ],
    () => {
      const urlParams = getCleanedUrlParams({
        page,
        orderBy: normalizeOrderBy(orderBy),
        order,
        pageSize: parseInt(pageSize, 10) || ARNM_MAINTENANCE_PAGE_SIZE,
        query,
      });
      const payload = getArnmMaintenancePayload(
        cleanedParams,
        filters.modalFiltersRules
      );

      return boardsServices.getBoardTasks({
        boardId: BOARDS_ID.ARM_MAINTENANCE,
        params: urlParams,
        payload,
      });
    },
    { enabled: router.isReady && isAssigneesFetched }
  );

  const enumsResponse = useQuery(
    [QUERY_KEYS.GET_BOARDS_ENUMS, BOARDS_ID.ARM_MAINTENANCE],
    () => boardsServices.getEnumsPerBoardId(BOARDS_ID.ARM_MAINTENANCE),
    { enabled: router.isReady }
  );

  useEffect(() => {
    if (maintenanceResponse?.error) {
      showError(maintenanceResponse?.error);
    }
  }, [maintenanceResponse?.error]);

  const resetModalChange = useCallback(() => {
    dispatch(resetModalArnmMaintenanceChange());
  }, [dispatch]);

  const handleFiltersModalChange = useCallback(
    (conditions) => {
      dispatch(filtersModalConditionArnmMaintenanceChange(conditions));
    },
    [dispatch]
  );

  const handleChangeRules = (rules) => {
    dispatch(filtersModalRulesArnmMaintenanceChange(rules));
  };

  const arnmMaintenance = handleArnmMaintenanceData(
    maintenanceResponse?.data?.data || [],
    assignees
  );
  const columns = getArnmMaintenanceColumns(arnmMaintenance);

  const getMaintenanceExportPayload = () => {
    return {
      boardId: BOARDS_ID.ARM_MAINTENANCE,
      payload: {
        type: 'csv',
        rule: getArnmMaintenancePayload(
          cleanedParams,
          filters.modalFiltersRules
        ),
      },
      params: getCleanedUrlParams({
        orderBy: normalizeOrderBy(orderBy),
        order,
        query,
      }),
    };
  };

  return {
    page,
    total: maintenanceResponse?.data?.meta?.total,
    pageSize: parseInt(pageSize, 10) || ARNM_MAINTENANCE_PAGE_SIZE,
    loading:
      maintenanceResponse?.isFetching ||
      isAssigneesFetching ||
      isServicePointsFetching,
    arnmMaintenance,
    servicePoints,
    columns,
    assignees,
    filters,
    assigneesWithNone,
    enums: enumsResponse?.data?.data || {},
    handleFiltersModalChange,
    handleChangeRules,
    resetModalChange,
    getArnmMaintenance: maintenanceResponse.refetch,
    getMaintenanceExportPayload,
  };
};
